import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, updateEvidence } from "@/lib/api";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["homicide", "cold_case", "fraud", "narcotics", "missing_person"];
const PRIORITIES = ["low", "medium", "high", "critical"];

export function NewCaseDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", summary: "", case_type: "homicide", priority: "medium", location: "", status: "active" });

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Case title is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/cases", form);
      toast.success("Case opened");
      setOpen(false);
      setForm({ title: "", summary: "", case_type: "homicide", priority: "medium", location: "", status: "active" });
      onCreated?.(data);
    } catch { toast.error("Could not open case"); }
    finally { setSaving(false); }
  };

  const field = "w-full bg-black border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-monk-blue focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-case-btn" className="flex items-center gap-2 bg-monk-blue text-white text-sm font-medium rounded-sm px-4 py-2.5 hover:bg-monk-blue/90 transition-colors">
          <Plus className="w-4 h-4" /> New Case
        </button>
      </DialogTrigger>
      <DialogContent className="bg-monk-panel border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Open New Case File</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">Register a new investigation. The Core Brain will route evidence once logged.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="overline block mb-1.5">Case Title</label>
            <input data-testid="case-title-input" className={field} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Homicide — Elm Street" /></div>
          <div><label className="overline block mb-1.5">Summary</label>
            <textarea data-testid="case-summary-input" rows={3} className={field} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Brief case context…" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline block mb-1.5">Type</label>
              <select data-testid="case-type-select" className={field} value={form.case_type} onChange={(e) => setForm({ ...form, case_type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select></div>
            <div><label className="overline block mb-1.5">Priority</label>
              <select data-testid="case-priority-select" className={field} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></div>
          </div>
          <div><label className="overline block mb-1.5">Location</label>
            <input data-testid="case-location-input" className={field} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Jurisdiction / area" /></div>
          <button onClick={submit} disabled={saving} data-testid="case-submit"
            className="w-full bg-monk-blue text-white rounded-sm py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-monk-blue/90 disabled:opacity-60 transition-colors">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</> : "Open Case"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddEvidenceDialog({ caseId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ label: "", kind: "note", content: "" });
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState("");

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.file_id); setFileName(data.filename);
      toast.success("Evidence file uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const submit = async () => {
    if (!form.label.trim()) { toast.error("Evidence label required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/evidence`, { ...form, file_id: fileId });
      toast.success("Evidence logged");
      setOpen(false); setForm({ label: "", kind: "note", content: "" }); setFileId(null); setFileName("");
      onAdded?.(data);
    } catch { toast.error("Could not log evidence"); }
    finally { setSaving(false); }
  };

  const field = "w-full bg-black border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-monk-blue focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-evidence-btn" className="flex items-center gap-2 border border-white/15 text-neutral-300 text-sm rounded-sm px-3 py-2 hover:text-white hover:border-white/30 transition-colors">
          <Plus className="w-4 h-4" /> Add Evidence
        </button>
      </DialogTrigger>
      <DialogContent className="bg-monk-panel border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Log Evidence</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">Add case notes or upload a scan. Original files are always retained.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="overline block mb-1.5">Label</label>
            <input data-testid="evidence-label-input" className={field} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Witness statement #3" /></div>
          <div><label className="overline block mb-1.5">Kind</label>
            <select data-testid="evidence-kind-select" className={field} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {["note", "document", "photo", "record", "transcript"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select></div>
          <div><label className="overline block mb-1.5">Content / Notes</label>
            <textarea data-testid="evidence-content-input" rows={4} className={field} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Paste case notes, transcript, or description…" /></div>
          <div>
            <label className="overline block mb-1.5">Attach File (optional)</label>
            <input data-testid="evidence-file-input" type="file" onChange={(e) => upload(e.target.files?.[0])}
              className="text-xs text-neutral-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border file:border-white/15 file:bg-black file:text-neutral-300 file:text-xs" />
            {fileName && <p className="text-[11px] text-monk-blue font-mono mt-1">{fileName}</p>}
          </div>
          <button onClick={submit} disabled={saving} data-testid="evidence-submit"
            className="w-full bg-monk-blue text-white rounded-sm py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-monk-blue/90 disabled:opacity-60 transition-colors">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Logging…</> : "Log Evidence"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditEvidenceDialog({ open, onOpenChange, caseId, evidence, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ label: "", tags: "", notes: "" });

  useEffect(() => {
    if (open) setForm({ label: evidence?.label || "", tags: (evidence?.tags || []).join(", "), notes: evidence?.notes || "" });
  }, [open, evidence]);

  const submit = async () => {
    if (!form.label.trim()) { toast.error("Evidence label required"); return; }
    setSaving(true);
    try {
      await updateEvidence(caseId, evidence.id, {
        label: form.label,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        notes: form.notes,
      });
      toast.success("Evidence metadata updated");
      onOpenChange(false);
      await onUpdated?.();
    } catch { toast.error("Could not update evidence"); }
    finally { setSaving(false); }
  };

  const field = "w-full bg-black border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-monk-blue focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-monk-panel border-white/10 text-white" data-testid="edit-evidence-dialog">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Edit Evidence Metadata</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">Labels, tags and notes only. Original files, content and engine findings are never altered — every edit is written to the chain of custody.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="overline block mb-1.5">Label</label>
            <input data-testid="edit-label-input" className={field} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
          <div><label className="overline block mb-1.5">Tags (comma-separated)</label>
            <input data-testid="edit-tags-input" className={field} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. priority, cipher, witness" /></div>
          <div><label className="overline block mb-1.5">Investigator Notes</label>
            <textarea data-testid="edit-notes-input" rows={4} className={field} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Annotations — kept separate from the forensic record…" /></div>
          <button onClick={submit} disabled={saving} data-testid="edit-evidence-submit"
            className="w-full bg-monk-blue text-white rounded-sm py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-monk-blue/90 disabled:opacity-60 transition-colors">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Metadata"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
